import React, { useEffect, useState } from 'react';

export type StudentChip = {
    id: number;
    firstName: string;
    lastName: string;
    score?: number;
};

// Show at most this many students when a dropdown is first opened; the rest sit
// behind a "+ N more" toggle so a large class doesn't blow out the panel height.
const CAP = 8;

const initials = (first?: string, last?: string) =>
    `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase();

// Shared expandable/searchable student roster used by the Focus Areas domain
// dropdowns and the Assignment Tracking dropdown. On open it shows up to 8
// students, a "+ N more" toggle for the rest, and a search box (to the right of
// the header text) to jump to a specific student. Search + expand state reset
// every time it mounts — i.e. each time a row is opened.
function StudentChipList({
    header,
    students,
    quad = false,
    showScore = false,
    sevOf,
    searchPlaceholder = 'Search student…',
}: {
    header: React.ReactNode;
    students: StudentChip[];
    quad?: boolean;
    showScore?: boolean;
    sevOf?: (score: number) => string;
    searchPlaceholder?: string;
}) {
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(false);
    // The detail panel only mounts when its row is expanded, so resetting on
    // mount gives a clean search/collapsed state on every open.
    useEffect(() => {
        setSearch('');
        setExpanded(false);
    }, []);

    const q = search.trim().toLowerCase();
    const filtered = q
        ? students.filter(s => `${s.firstName} ${s.lastName}`.toLowerCase().includes(q))
        : students;
    const shown = expanded ? filtered : filtered.slice(0, CAP);
    const hidden = filtered.length - CAP;

    return (
        <>
            <div className="cdr__detail-head-row">
                <div className="cdr__detail-head">{header}</div>
                <div className="cdr__search cdr__search--sm">
                    <input
                        placeholder={searchPlaceholder}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
            </div>
            <div className={`cdr__student-grid${quad ? ' cdr__student-grid--quad' : ''}${expanded ? ' expanded' : ''}`}>
                {shown.map(s => (
                    <div
                        key={s.id}
                        className={`cdr__student-row${showScore && sevOf && s.score != null ? ' ' + sevOf(s.score) : ''}`}
                    >
                        <span className="cdr__avatar">{initials(s.firstName, s.lastName)}</span>
                        <span className="cdr__student-name">{s.firstName} {s.lastName}</span>
                        {showScore && s.score != null && <span className="cdr__student-score">{s.score}%</span>}
                    </div>
                ))}
            </div>
            {shown.length === 0 && (
                <div className="cdr__detail-note">No students match “{search}”.</div>
            )}
            {!expanded && hidden > 0 && (
                <button type="button" className="cdr__weaklist-more" onClick={() => setExpanded(true)}>
                    + {hidden} more
                </button>
            )}
            {expanded && filtered.length > CAP && (
                <button type="button" className="cdr__weaklist-more" onClick={() => setExpanded(false)}>
                    Show less
                </button>
            )}
        </>
    );
}

export default StudentChipList;
